"use client";

import { useState } from "react";
import { useTranslations } from "use-intl";

import { CompanyIcon, SellIcon } from "@/components/appIcons";
import type { FilterOption } from "@/components/ui/filter-select";
import { MultiFilterSelect } from "@/components/ui/multi-filter-select";
import type { Filter } from "@garimpa/lib/facetFilter";

import { useSearchFacetOptions } from "./use-search-facet-options";

export function SearchOrganizationFilters({
  label,
  company,
  labelCounts,
  companyCounts,
  filter,
  onLabelChange,
  onCompanyChange,
}: {
  label: string[];
  company: string[];
  labelCounts: { value: string; count: number }[];
  companyCounts: { value: string; count: number }[];
  filter: Filter | undefined;
  onLabelChange: (labels: string[]) => void;
  onCompanyChange: (companies: string[]) => void;
}) {
  const t = useTranslations("globals.search");
  const trf = useTranslations("shared.releaseFilters");
  const [labelSearch, setLabelSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const labelOptions = useSearchFacetOptions({
    facet: "label",
    filter,
    initialItems: labelCounts,
    query: labelSearch,
    selectedValues: label,
  });
  const companyOptions = useSearchFacetOptions({
    facet: "company",
    filter,
    initialItems: companyCounts,
    query: companySearch,
    selectedValues: company,
  });
  return (
    <>
      <hr className="border-border" />
      <div>
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <SellIcon className="text-sm" /> {t("labels")}
        </h3>
        <MultiFilterSelect
          icon={SellIcon}
          options={labelOptions.items.map(
            (item): FilterOption => ({ value: item.value, label: item.value, count: item.count }),
          )}
          value={label}
          onChange={onLabelChange}
          placeholder={trf("labelFilter")}
          searchable
          filterSearchOptions={!labelOptions.remoteSearchActive}
          onSearchChange={setLabelSearch}
          searchPlaceholder={trf("searchLabel")}
          variant="fullWidth"
          showTriggerIcon={false}
        />
      </div>
      <div>
        <h3 className="text-sm font-bold mb-3 flex items-center gap-2">
          <CompanyIcon className="text-sm" /> {t("companies")}
        </h3>
        <MultiFilterSelect
          icon={CompanyIcon}
          options={companyOptions.items.map(
            (item): FilterOption => ({ value: item.value, label: item.value, count: item.count }),
          )}
          value={company}
          onChange={onCompanyChange}
          placeholder={trf("companyFilter")}
          searchable
          filterSearchOptions={!companyOptions.remoteSearchActive}
          onSearchChange={setCompanySearch}
          searchPlaceholder={trf("searchCompany")}
          variant="fullWidth"
          showTriggerIcon={false}
        />
      </div>
    </>
  );
}
